import React, { useEffect, useState, Suspense } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  changeAirport,
  changeDestinationAirport,
  changeOriginAirport,
} from "../features/searchFlights/searchFlightsSlice";
import {
  selectConnectionMarkers,
  selectDirectMarkers,
  selectLoadingMarkers,
} from "../features/marker/markerSlice";
import { useTranslation } from "react-i18next";
import {
  trackMapLoaded,
  trackerFindAirportServed,
  trackerFindDestinations,
} from "../features/analytics/AnalyticsActions";
import { MapboxConfig } from "./mapboxTypes";
import { getStartUpMarkers } from "../features/marker/functions";
import { fetchDirectMarkers, fetchMarkers } from "../features/marker/actions";
import { RMLocation } from "../types";
import { fetchResults } from "../features/searchFlights/SearchFlightsActions";

const Mapbox = React.lazy(() => import("./MapboxMapGl"));

function findOriginCriteria(requests) {
  if (requests.directMarkersAirport) {
    return requests.directMarkersAirport.request.originCriteria;
  } else {
    return null;
  }
}

function MapboxMapWrapper() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const [requests, setRequests] = useState(null);
  const [selectedDot, setSelectedDot] = useState(null);
  const config = useAppSelector((state) => state.config);
  const originAirport = useAppSelector(
    (state) => state.searchFlights.form.originAirport
  );
  const destinationAirport = useAppSelector(
    (state) => state.searchFlights.form.destinationAirport
  );
  const viaAirport = useAppSelector(
    (state) => state.searchFlights.form.viaAirport
  );
  const startAirport = useAppSelector(
    (state) => state.searchFlights.startAirport
  );
  const form = useAppSelector((state) => state.searchFlights.form);
  const disabled = useAppSelector((state) => state.searchFlights.form.disabled);
  const display = useAppSelector((state) => state.searchFlights.display);
  const direct = useAppSelector(selectDirectMarkers);
  const connection = useAppSelector(selectConnectionMarkers);
  const loadingMarkers = useAppSelector(selectLoadingMarkers);
  const allowSameDestination = useAppSelector(
    (state) => state.config.mapper.allowSameDestination
  );

  const [mapboxConfig, setMapboxConfig] = useState<MapboxConfig>();
  let defaultOriginAirport = originAirport;
  let defaultDestAirport = destinationAirport;

  useEffect(() => {
    if (selectedDot) {
      const airport = JSON.parse(selectedDot.location);
      if (config.app.customerType.airline) {
        if (defaultOriginAirport && defaultDestAirport) {
          defaultOriginAirport = null;
          defaultDestAirport = null;
        }
        if (defaultOriginAirport === null && defaultDestAirport === null) {
          defaultOriginAirport = airport;
          getNewFetchMarks(airport);
          dispatch(
            trackerFindDestinations(
              "marker_selection",
              requests.directMarkers.request
            )
          );
        } else {
          if (
            !allowSameDestination &&
            defaultOriginAirport &&
            defaultOriginAirport.port.code === airport.port.code
          ) {
            return;
          }
          if (
            defaultOriginAirport &&
            defaultOriginAirport.port.code === airport.port.code
          ) {
            getNewFetchMarks(airport);
          } else if (defaultDestAirport === null) {
            defaultDestAirport = airport;
            dispatch(changeDestinationAirport(airport));
            dispatch(fetchResults({ queryTrigger: "map" }));
          }
        }
      } else {
        if (
          !allowSameDestination &&
          originAirport &&
          originAirport.port.code === airport.port.code
        ) {
          return;
        }
        if (
          !allowSameDestination &&
          destinationAirport &&
          destinationAirport.port.code === airport.port.code
        ) {
          return;
        }
        dispatch(changeAirport(airport));
        // check that all fields are fill before fetching results
        // it's minus 1 airport as the current form has not the result from above
        const expected = form.direction === "via" ? 2 : 1;
        let actual = 0;
        if (form.originAirport) actual++;
        if (form.destinationAirport) actual++;
        if (form.direction === "via" && form.viaAirport) actual++;
        if (expected <= actual) {
          dispatch(fetchResults({ queryTrigger: "map" }));
        }
      }
      setSelectedDot(null);
    }
  }, [selectedDot]);

  const getNewFetchMarks = (airport: RMLocation) => {
    const newRequests = getStartUpMarkers({
      appConfig: config.app,
      mapper: config.mapper,
      cbwsRequest: config.cbwsRequest,
      departingDate: form.departingDate || new Date().getTime(),
      returningDate: form.returningDate || new Date().getTime(),
      startAirport: airport.port.code,
      isDirectMarkers: false,
    });
    dispatch(changeOriginAirport(airport));
    dispatch(fetchMarkers({ requests: newRequests, appConfig: config.app }));
  };

  const onMapLoad = () => {
    dispatch(trackMapLoaded(config.map));
  };

  useEffect(() => {
    if (config.loaded) {
      const newRequests = getStartUpMarkers({
        appConfig: config.app,
        mapper: config.mapper,
        cbwsRequest: config.cbwsRequest,
        departingDate: form.departingDate || new Date().getTime(),
        returningDate: form.returningDate || new Date().getTime(),
        startAirport,
        isDirectMarkers: config.app.customerType.airline ? true : false,
      });
      const newOriginCriteria = findOriginCriteria(newRequests);
      const oldOriginCriteria =
        requests !== null ? findOriginCriteria(requests) : null;
      if (requests === null || newOriginCriteria !== oldOriginCriteria) {
        setRequests(newRequests);
        setLoading(true);
      }
    }
  }, [config, form.departingDate, form.returningDate, startAirport, requests]);

  useEffect(() => {
    if (loading && requests) {
      if (config.app.customerType.airline) {
        dispatch(
          fetchDirectMarkers({
            request: requests.directMarkers.request,
            appConfig: config.app,
          })
        ).then(() => {
          dispatch(
            trackerFindAirportServed("startup", requests.directMarkers.request)
          );
        });
      } else {
        dispatch(
          fetchMarkers({ requests: requests, appConfig: config.app })
        ).then(() => {
          dispatch(
            trackerFindDestinations(
              "startup",
              requests.directMarkersAirport.request
            )
          );
        });
      }
      setLoading(false);
    }
  }, [config.app, loading, requests, dispatch]);

  useEffect(() => {
    setMapboxConfig({
      ...config.mapper,
      ...config.map,
    });
  }, [config.mapper, config.map]);

  if (!mapboxConfig) {
    return <div />;
  }

  return (
    <Suspense
      fallback={
        <div
          style={{
            paddingBottom: "56.25%",
            position: "relative",
          }}
        >
          <div className="box map-loading has-text-centered">
            <span>{t("LOADING_MAP")}</span>
            <progress className="progress" />
          </div>
        </div>
      }
    >
      <Mapbox
        config={mapboxConfig}
        direct={direct}
        connection={connection}
        origin={
          viaAirport
            ? viaAirport
            : destinationAirport?.readOnly
            ? destinationAirport
            : originAirport
        }
        destination={
          viaAirport
            ? viaAirport
            : destinationAirport?.readOnly
            ? originAirport
            : destinationAirport
        }
        layout={config.app.lfMain}
        fitMarkers={display === "flights" && disabled}
        onMapLoad={onMapLoad}
        onDotClick={setSelectedDot}
        mapContainerStyle={
          config.app.lfMain !== "FLOAT"
            ? {
                paddingBottom: "56.25%",
                position: "relative",
              }
            : {}
        }
      >
        {loadingMarkers && (
          <div className="box map-loading has-text-centered">
            <span>{t("LOADING_MARKERS")}</span>
            <progress className="progress" />
          </div>
        )}
        <div className="mapbox-dots-legend">
          {(mapboxConfig.intialiseStartUpPoints.directs ||
            (direct && direct.markers && direct.markers.length > 0)) && (
            <div className="map-legend-item">
              <span
                id="direct-flight-dot"
                className="airport-marker-dot"
                style={{
                  backgroundColor:
                    config.mapper.directIconStyle["circle-color"],
                  width: config.mapper.directIconStyle["circle-radius"] * 2,
                  height: config.mapper.directIconStyle["circle-radius"] * 2,
                }}
              />
              <span className="map-legend-label">{t("DIRECTS")}</span>
            </div>
          )}
          {(mapboxConfig.intialiseStartUpPoints.connections ||
            (connection &&
              connection.markers &&
              connection.markers.length > 0)) && (
            <div className="map-legend-item">
              <span
                id="connecting-flight-dot"
                className="airport-marker-dot"
                style={{
                  backgroundColor:
                    config.mapper.connectionIconStyle["circle-color"],
                  width: config.mapper.connectionIconStyle["circle-radius"] * 2,
                  height:
                    config.mapper.connectionIconStyle["circle-radius"] * 2,
                }}
              />
              <span className="map-legend-label">{t("CONNECTIONS")}</span>
            </div>
          )}
        </div>
      </Mapbox>
    </Suspense>
  );
}

export default React.memo(MapboxMapWrapper);
